/**
 * v2 디자인 시스템 — CSS 변수 브리지
 *
 * 컴포넌트는 브랜드 색을 리터럴이 아니라 `v2Color.*`(= `var(--v2-*)`)로 참조한다.
 * 실제 값은 `<V2BrandScope brand mode>`(또는 앱 루트 전역 주입)가 `brandVars(brand, mode)`로 주입한다.
 * → 컴포넌트/레거시 테마 alias 한 벌로 여러 브랜드×모드를 커버.
 *
 * 구조 토큰(spacing/radius/typography/layout)은 브랜드 무관이라 CSS 변수 없이
 * foundation.ts에서 리터럴로 그대로 쓴다. scale은 카탈로그 전용이라 변수 제외.
 */
import { checkIsInBubbleTapApp } from '@repo/utils';

import { V2Brand, V2Mode, V2Palette, resolvePalette } from './brand';

/**
 * 컴포넌트/레거시 alias가 import 하는 색 참조(모두 `var(--v2-*)`). 팔레트 color/gradient 미러.
 */
export const v2Color = {
  brand: {
    primary: 'var(--v2-brand-primary)',
    primaryPressed: 'var(--v2-brand-primary-pressed)',
    secondary: 'var(--v2-brand-secondary)',
    pinkPastel: 'var(--v2-brand-pink-pastel)',
    ink: 'var(--v2-brand-ink)',
    /** 마케팅 지면·로고 전용(UI 내 사용 금지). 팅글만 정의 — 버블챗에선 변수 자체가 없다 */
    marketing: 'var(--v2-brand-marketing)',
  },
  bg: {
    primary: 'var(--v2-bg-primary)',
    secondary: 'var(--v2-bg-secondary)',
    tertiary: 'var(--v2-bg-tertiary)',
    elevated: 'var(--v2-bg-elevated)',
    muted: 'var(--v2-bg-muted)',
    dim: 'var(--v2-bg-dim)',
    dim2: 'var(--v2-bg-dim-2)',
    /** 페이지 최하단 배경 — 브랜드×표면(V2Surface)으로 갈린다. 분기는 `paletteVars`의 `--v2-page-bg` 한 곳 */
    page: 'var(--v2-page-bg)',
    /** 페이지 위에 얹히는 패널(카드) 면 — page 와 같은 축으로 한 단 위를 가리킨다(`--v2-panel-bg`) */
    panel: 'var(--v2-panel-bg)',
  },
  label: {
    primary: 'var(--v2-label-primary)',
    secondary: 'var(--v2-label-secondary)',
    tertiary: 'var(--v2-label-tertiary)',
    quaternary: 'var(--v2-label-quaternary)',
    onAccent: 'var(--v2-label-on-accent)',
  },
  separator: 'var(--v2-separator)',
  border: {
    default: 'var(--v2-border-default)',
    subtle: 'var(--v2-border-subtle)',
    strong: 'var(--v2-border-strong)',
    black: {
      default: 'var(--v2-border-black-default)',
      subtle: 'var(--v2-border-black-subtle)',
      strong: 'var(--v2-border-black-strong)',
    },
  },
  system: {
    red: 'var(--v2-system-red)',
    yellow: 'var(--v2-system-yellow)',
    green: 'var(--v2-system-green)',
    blue: 'var(--v2-system-blue)',
    nsfw: 'var(--v2-system-nsfw)',
  },
  static: {
    white: 'var(--v2-static-white)',
    black: 'var(--v2-static-black)',
  },
  text: {
    accent: 'var(--v2-text-accent)',
  },
  chat: {
    bubbleIn: 'var(--v2-chat-bubble-in)',
    bubbleOut: 'var(--v2-chat-bubble-out)',
  },
  currency: {
    coin: 'var(--v2-currency-coin)',
    potion: 'var(--v2-currency-potion)',
  },
  button: {
    subBg: 'var(--v2-button-sub-bg)',
  },
  highlight: {
    primary: {
      '10': 'var(--v2-highlight-primary-10)',
      '30': 'var(--v2-highlight-primary-30)',
      '50': 'var(--v2-highlight-primary-50)',
    },
    secondary: {
      '10': 'var(--v2-highlight-secondary-10)',
      '30': 'var(--v2-highlight-secondary-30)',
      '50': 'var(--v2-highlight-secondary-50)',
    },
    error: {
      '10': 'var(--v2-highlight-error-10)',
      '30': 'var(--v2-highlight-error-30)',
      '50': 'var(--v2-highlight-error-50)',
    },
    success: {
      '10': 'var(--v2-highlight-success-10)',
      '30': 'var(--v2-highlight-success-30)',
      '50': 'var(--v2-highlight-success-50)',
    },
    warning: {
      '10': 'var(--v2-highlight-warning-10)',
      '30': 'var(--v2-highlight-warning-30)',
      '50': 'var(--v2-highlight-warning-50)',
    },
    info: {
      '10': 'var(--v2-highlight-info-10)',
      '30': 'var(--v2-highlight-info-30)',
      '50': 'var(--v2-highlight-info-50)',
    },
  },
  gradient: {
    brandGlow: 'var(--v2-gradient-brand-glow)',
    aurora: 'var(--v2-gradient-aurora)',
    hot: 'var(--v2-gradient-hot)',
    spectrum: 'var(--v2-gradient-spectrum)',
    orb: 'var(--v2-gradient-orb)',
    /** 프로모 스낵바 배경 — 팅글/버블챗 모두 정의 */
    promo: 'var(--v2-gradient-promo)',
  },
} as const;

/**
 * 변수가 주입되는 표면. 브랜드×모드 팔레트만으로는 정해지지 않는 파생 변수(`--v2-page-bg`·`--v2-panel-bg`)의 분기 축.
 * - `browser`: 일반 웹. 버블챗은 리브랜딩 비대상이라 기존 다크 배경(bg/primary)을 유지한다.
 * - `inApp`: 하이브리드 앱 웹뷰. 네이티브 버블챗은 모든 화면을 background/secondary(#242526) 위에 그리고
 *   (iOS BaseViewController `.backgroundSub`, Android 테마 windowBackground) 카드는 tertiary 를 쓴다.
 *   웹이 primary 를 쓰면 알림 탭(popup.background=elevated 로 전체를 칠함)만 네이티브 색이고 나머지
 *   탭은 더 어두운 브라우저 색이라 탭마다 배경이 달라 보였다. 팅글은 두 표면이 같다.
 */
export type V2Surface = 'browser' | 'inApp';

/** 현재 문서가 놓인 표면. 하이브리드 앱 UA 면 `inApp`. (SSR·node 테스트처럼 window 가 없으면 `browser`) */
export const detectV2Surface = (): V2Surface =>
  typeof window !== 'undefined' && checkIsInBubbleTapApp() ? 'inApp' : 'browser';

/** `name: value` 한 줄 — 값이 있을 때만. (undefined optional 필드는 건너뜀) */
const decl = (name: string, value: string | undefined): string => (value == null ? '' : `${name}: ${value};`);

/**
 * 브랜드×모드 팔레트를 CSS custom property 선언 문자열로.
 * `V2BrandScope`의 styled / 앱 루트 전역 주입에서 사용. v2Color의 변수명과 1:1 대응.
 * `surface` 기본값은 현재 문서에서 감지한 표면 — 스코프 주입(V2BrandScope·오버레이)이 전역 주입과 같은 값을 갖는다.
 */
export function brandVars(brand: V2Brand, mode?: V2Mode, surface: V2Surface = detectV2Surface()): string {
  return paletteVars(resolvePalette(brand, mode), brand, surface);
}

/** 페이지 최하단 배경 — 팅글 elevated, 버블챗은 브라우저 primary · 인앱 secondary(근거는 V2Surface 주석) */
const pageBg = (c: V2Palette['color'], brand: V2Brand, surface: V2Surface): string => {
  if (brand === 'tingle') return c.bg.elevated;

  return surface === 'inApp' ? c.bg.secondary : c.bg.primary;
};

/** 페이지 위 패널(카드) 면 — 페이지 배경보다 한 단 위. 팅글 tertiary, 버블챗은 브라우저 secondary · 인앱 tertiary */
const panelBg = (c: V2Palette['color'], brand: V2Brand, surface: V2Surface): string => {
  if (brand === 'tingle') return c.bg.tertiary;

  return surface === 'inApp' ? c.bg.tertiary : c.bg.secondary;
};

/**
 * 팔레트 → CSS custom property 선언 문자열. v2Brands에 등록되지 않은 파생 팔레트
 * (리워드 웹의 `affiliatePalette` 등)를 주입할 때 사용한다.
 *
 * `brand`는 팔레트만으로는 결정할 수 없는 분기(`--v2-page-bg`)에 필요하다. 파생 팔레트도
 * 어느 브랜드용인지는 호출부가 알고 있으므로 필수 인자로 받는다.
 * `surface`도 같은 분기 축이다(기본은 현재 문서에서 감지). 리워드 웹처럼 인앱에서도 브라우저 면을
 * 유지해야 하는 곳은 `'browser'`를 명시한다.
 */
export function paletteVars(palette: V2Palette, brand: V2Brand, surface: V2Surface = detectV2Surface()): string {
  const { color: c, gradient: g } = palette;
  const hl = c.highlight;
  const highlightDecls = (['primary', 'secondary', 'error', 'success', 'warning', 'info'] as const).flatMap((k) =>
    (['10', '30', '50'] as const).map((op) => decl(`--v2-highlight-${k}-${op}`, hl[k][op]))
  );
  return [
    decl('--v2-brand-primary', c.brand.primary),
    decl('--v2-brand-primary-pressed', c.brand.primaryPressed),
    decl('--v2-brand-secondary', c.brand.secondary),
    decl('--v2-brand-pink-pastel', c.brand.pinkPastel),
    decl('--v2-brand-ink', c.brand.ink),
    decl('--v2-brand-marketing', c.brand.marketing),
    decl('--v2-bg-primary', c.bg.primary),
    decl('--v2-bg-secondary', c.bg.secondary),
    decl('--v2-bg-tertiary', c.bg.tertiary),
    decl('--v2-bg-elevated', c.bg.elevated),
    decl('--v2-bg-muted', c.bg.muted),
    decl('--v2-bg-dim', c.bg.dim),
    decl('--v2-bg-dim-2', c.bg.dim2),
    // 페이지 최하단 배경. 팅글 개편 시안은 background/elevated(홈 884:79607 실측).
    // 버블챗 브라우저는 리브랜딩 대상이 아니라 기존대로 background/primary 를 유지한다
    // (elevated 로 두면 다크가 #131415 → #242526 으로 밝아진다). 버블챗 인앱은 네이티브와 같은
    // background/secondary(#242526) — 근거는 V2Surface 주석.
    decl('--v2-page-bg', pageBg(c, brand, surface)),
    // 페이지 위 패널(카드) 면. 팅글은 tertiary(chatServiceColor field.side 주석), 버블챗 브라우저는
    // 기존 구현값 secondary, 인앱은 페이지가 secondary 로 올라가므로 네이티브 카드와 같은
    // tertiary(Android top_my_profile 의 background_tertiary)로 한 단 따라 올라간다.
    decl('--v2-panel-bg', panelBg(c, brand, surface)),
    decl('--v2-label-primary', c.label.primary),
    decl('--v2-label-secondary', c.label.secondary),
    decl('--v2-label-tertiary', c.label.tertiary),
    decl('--v2-label-quaternary', c.label.quaternary),
    decl('--v2-label-on-accent', c.label.onAccent),
    decl('--v2-separator', c.separator),
    decl('--v2-border-default', c.border.default),
    decl('--v2-border-subtle', c.border.subtle),
    decl('--v2-border-strong', c.border.strong),
    decl('--v2-border-black-default', c.border.black.default),
    decl('--v2-border-black-subtle', c.border.black.subtle),
    decl('--v2-border-black-strong', c.border.black.strong),
    decl('--v2-system-red', c.system.red),
    decl('--v2-system-yellow', c.system.yellow),
    decl('--v2-system-green', c.system.green),
    decl('--v2-system-blue', c.system.blue),
    decl('--v2-system-nsfw', c.system.nsfw),
    decl('--v2-static-white', c.static.white),
    decl('--v2-static-black', c.static.black),
    decl('--v2-text-accent', c.text.accent),
    decl('--v2-chat-bubble-in', c.chat.bubbleIn),
    decl('--v2-chat-bubble-out', c.chat.bubbleOut),
    decl('--v2-currency-coin', c.currency.coin),
    decl('--v2-currency-potion', c.currency.potion),
    decl('--v2-button-sub-bg', c.button.subBg),
    decl('--v2-gradient-brand-glow', g?.brandGlow),
    decl('--v2-gradient-aurora', g?.aurora),
    decl('--v2-gradient-hot', g?.hot),
    decl('--v2-gradient-spectrum', g?.spectrum),
    decl('--v2-gradient-orb', g?.orb),
    decl('--v2-gradient-promo', g?.promo),
    decl('--v2-hero-scrim', palette.heroScrim),
    ...highlightDecls,
  ]
    .filter(Boolean)
    .join('\n');
}
